import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function AboutPage() {
  const [contactFormStatus, setContactFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [contactError, setContactError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotValue = (formData.get('company_alt') as string || '').trim();

    if (honeypotValue) {
      setContactFormStatus('success');
      form.reset();
      return;
    }

    setContactFormStatus('idle');
    setContactError('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qrqinnahb7382ovee0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: formData.get('name') as string,
          email: formData.get('email') as string,
          message: formData.get('message') as string,
        }).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setContactFormStatus('success');
        form.reset();
      } else if (serverMsg && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
        setContactFormStatus('error');
        setContactError(serverMsg);
      } else {
        setContactFormStatus('error');
        setContactError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setContactFormStatus('error');
      setContactError('网络错误，请检查网络连接');
    }
  };

  const faqData = [
    {
      question: '黑料社的内容来源是什么？',
      answer: '黑料社拥有一支经验丰富的调查记者团队，同时接收来自各行各业的内部人士投稿爆料。所有内容在发布前均经过严格的真实性审核流程，确保信息的可靠性和准确性。',
    },
    {
      question: '黑料社如何保证爆料的真实性？',
      answer: '我们采用多重验证机制：首先由专业编辑对爆料信息进行初步筛选，然后由调查团队进行交叉验证，最后需要至少两个独立信息源确认后才予以发布。对于未经充分验证的信息，我们会明确标注"待核实"。',
    },
    {
      question: '如何向黑料社投稿爆料？',
      answer: '你可以通过本页的联系表单、官方邮箱（tips@heiliaoshe.com）或社交媒体私信向我们提供爆料线索。我们承诺对所有爆料人的身份信息严格保密，绝不泄露来源。',
    },
    {
      question: '黑料社会不会因为爆料被起诉？',
      answer: '我们严格遵守国家法律法规，所有报道均基于真实可靠的信息源，并保留完整的证据链。我们设有专业的法务团队，确保每一篇报道都有充分的法律依据。我们坚持事实，不畏压力。',
    },
    {
      question: '如何成为黑料社的调查记者？',
      answer: '黑料社持续招募有新闻理想和调查能力的记者。如果你具备出色的调查能力、敏锐的新闻嗅觉和扎实的文字功底，欢迎将简历和代表作品发送至 hr@heiliaoshe.com。',
    },
  ];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于黑料社 - 品牌介绍 | 独家猛料曝光平台"
        description="了解黑料社的品牌故事、团队背景、媒体声誉和联系方式。黑料社是专注独家内幕、热点曝光与深度八卦的聚合平台。"
        keywords="黑料社,关于黑料社,黑料社品牌,黑料社联系方式,黑料社团队"
        canonicalUrl="https://heiliaoshe.com/about"
        ogType="website"
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://heiliaoshe.com/' },
            { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://heiliaoshe.com/about' },
          ],
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于黑料社',
          url: 'https://heiliaoshe.com/about',
          isPartOf: { '@type': 'WebSite', name: '黑料社' },
          description: '了解黑料社的品牌故事、团队背景和联系方式',
        }}
      />
      <SchemaOrg
        type="FAQPage"
        data={{
          mainEntity: faqData.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />

            {/* Brand Introduction */}
            <section className="mb-16">
              <h1 className="text-3xl md:text-5xl font-bold text-foreground-950 font-heading text-center mb-6">
                关于黑料社
              </h1>
              <p className="text-lg text-foreground-600 text-center max-w-3xl mx-auto leading-relaxed">
                黑料社成立于2024年，是中文互联网最具影响力的独家内幕与热点曝光平台。我们秉持"真相至上"的理念，致力于为公众提供真实、深度、独家的爆料内容。
              </p>
            </section>

            {/* Mission & Values */}
            <section className="mb-16">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="p-8 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-4">
                    <i className="ri-search-eye-line text-primary-600 text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-foreground-950 font-heading mb-3">追求真相</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    我们坚信公众有知情权。每一篇报道都经过严格的事实核查，层层把关，确保信息的真实性和可靠性。
                  </p>
                </div>
                <div className="p-8 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-accent-100 mb-4">
                    <i className="ri-shield-check-line text-accent-600 text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-foreground-950 font-heading mb-3">保护信源</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    保护爆料人是我们的底线。无论面对何种压力，我们绝不透露任何信息提供者的身份，这是我们最重要的职业操守。
                  </p>
                </div>
                <div className="p-8 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 mb-4">
                    <i className="ri-flashlight-line text-secondary-600 text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-foreground-950 font-heading mb-3">不畏权势</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    面对权势，我们从不退缩。黑料社最重要的原则就是：只对真相负责，不对权力低头。
                  </p>
                </div>
              </div>
            </section>

            {/* Reputation */}
            <section className="mb-16">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-8 text-center">
                媒体声誉
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="p-6 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <div className="text-3xl font-bold text-primary-500 font-heading mb-1">500万+</div>
                  <p className="text-sm text-foreground-500">日均阅读量</p>
                </div>
                <div className="p-6 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <div className="text-3xl font-bold text-accent-500 font-heading mb-1">120+</div>
                  <p className="text-sm text-foreground-500">独家爆料</p>
                </div>
                <div className="p-6 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <div className="text-3xl font-bold text-primary-500 font-heading mb-1">98%</div>
                  <p className="text-sm text-foreground-500">报道准确率</p>
                </div>
                <div className="p-6 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <div className="text-3xl font-bold text-accent-500 font-heading mb-1">50+</div>
                  <p className="text-sm text-foreground-500">合作媒体</p>
                </div>
              </div>

              <div className="mt-10 p-8 rounded-lg bg-accent-100/50 border border-accent-200/50">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-500">
                    <i className="ri-double-quotes-l text-background-50 text-lg"></i>
                  </div>
                  <blockquote>
                    <p className="text-foreground-800 leading-relaxed italic">
                      "黑料社是目前国内最具影响力的独立爆料平台之一，其报道以深度、独家、真实著称，在业内享有极高的公信力和口碑。多家主流媒体均与其建立了内容合作机制。"
                    </p>
                    <cite className="text-sm text-foreground-500 mt-3 block not-italic">
                      —— 《中国媒体观察》2026年度独立媒体报告
                    </cite>
                  </blockquote>
                </div>
              </div>
            </section>

            {/* Contact */}
            <section className="mb-16">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-8 text-center">
                联系我们
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                        <i className="ri-mail-line text-primary-600 text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground-900 font-heading">爆料投稿</h4>
                        <p className="text-sm text-foreground-500">tips@heiliaoshe.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100">
                        <i className="ri-customer-service-line text-accent-600 text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground-900 font-heading">商务合作</h4>
                        <p className="text-sm text-foreground-500">biz@heiliaoshe.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100">
                        <i className="ri-briefcase-line text-secondary-600 text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground-900 font-heading">加入我们</h4>
                        <p className="text-sm text-foreground-500">hr@heiliaoshe.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                        <i className="ri-map-pin-line text-primary-600 text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground-900 font-heading">办公地址</h4>
                        <p className="text-sm text-foreground-500">北京市朝阳区建国路88号SOHO现代城A座</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <form
                    onSubmit={handleContactSubmit}
                    data-readdy-form
                    id="contact-form-about"
                    className="p-6 rounded-lg bg-background-100 border border-background-200/70"
                  >
                    <h3 className="text-lg font-bold text-foreground-950 font-heading mb-5">给我们留言</h3>

                    <div className="form-honeypot-wrapper" aria-hidden="true">
                      <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" readOnly />
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label htmlFor="contact-name" className="text-sm font-medium text-foreground-700 block mb-1">
                          姓名
                        </label>
                        <input
                          id="contact-name"
                          type="text"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入你的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="text-sm font-medium text-foreground-700 block mb-1">
                          邮箱
                        </label>
                        <input
                          id="contact-email"
                          type="email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入你的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="text-sm font-medium text-foreground-700 block mb-1">
                          留言内容
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                          placeholder="请输入你想说的话，不超过500字"
                        ></textarea>
                      </div>
                      <button
                        type="submit"
                        className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
                      >
                        提交留言
                      </button>
                      {contactFormStatus === 'success' && (
                        <p className="text-sm text-accent-600 text-center">留言已成功提交，感谢你的反馈！</p>
                      )}
                      {contactFormStatus === 'error' && (
                        <p className="text-sm text-primary-500 text-center">{contactError}</p>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section className="mb-16">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-8 text-center">
                常见问题
              </h2>
              <div className="space-y-4 max-w-3xl mx-auto">
                {faqData.map((faq, index) => (
                  <details key={index} className="group p-6 rounded-lg bg-background-100 border border-background-200/70 cursor-pointer">
                    <summary className="flex items-center justify-between font-semibold text-foreground-900 font-heading text-base">
                      {faq.question}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform text-lg"></i>
                    </summary>
                    <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </details>
                ))}
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}